import {
  Box,
  IconButton as MuiIconButton,
  Typography,
  Collapse,
  useTheme
} from '@mui/material'

import { ClockIcon, CloseIcon, MemberDownIcon } from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import {
  EventDatePicker,
  PickerType
} from 'components/eventComponents/EventDatePicker'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { IEventDetails } from 'hooks/useEventDetails'
import { getDefaultTimezone } from 'index'
import i18n from 'i18n'
import { t } from 'i18next'
import dayjs from 'dayjs'
import { Controller } from 'react-hook-form'
import { EventDoc } from 'types/api/changes'
import { Model } from 'types/model'
import { useLayoutEffect, useState } from 'react'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import TimezonePopup from './TimezonePopup'

import { isAMPM } from 'utils/date'
import { isMobileDevice } from 'utils/constants'

dayjs.extend(utc)
dayjs.extend(timezone)

interface StartEndDateRowsProps {
  eventHook: IEventDetails
  event: Model<EventDoc> | EventDoc
  timeZoneOpen: boolean
  setTimeZoneOpen: (open: boolean) => void
  removeTimeZone: () => void
  nonDefaultTimeZone: boolean
  initiallyExpanded?: boolean
  readonly?: boolean
}

// Format date based on mode: collapsed (year only if different) or expanded (always year)
function formatDate(
  timestamp: number,
  timeZone: string | null | undefined,
  showYear: boolean
): string {
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const date = dayjs(timestamp).tz(tz)
  const currentYear = dayjs().year()
  const dateYear = date.year()

  // Format: "Wed, May 27" or "Wed, May 27, 2027"
  if (showYear || dateYear !== currentYear) {
    return date.format('ddd, MMM D, YYYY')
  }
  return date.format('ddd, MMM D')
}

// Format time with lowercase am/pm
function formatTime(
  timestamp: number,
  timeZone: string | null | undefined
): string {
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const date = dayjs(timestamp).tz(tz)

  if (!isAMPM()) {
    return date.format('HH:mm')
  }

  // Format with lowercase am/pm
  const hours = date.hour()
  const minutes = date.minute()
  const period = hours >= 12 ? 'pm' : 'am'
  const displayHours = hours % 12 || 12
  const minutesStr = minutes.toString().padStart(2, '0')

  return `${displayHours}:${minutesStr}${period}`
}

export default function StartEndDateRows(props: StartEndDateRowsProps) {
  const {
    eventHook,
    event,
    timeZoneOpen,
    setTimeZoneOpen,
    removeTimeZone,
    nonDefaultTimeZone,
    initiallyExpanded
  } = props
  const theme = useTheme()

  const [expanded, setExpanded] = useState(initiallyExpanded || false)

  const isAllDay = eventHook.getValues('AllDay') === '1'
  const somedayValue = eventHook.getValues('someday')
  const isSomeday = somedayValue === '1'
  const timeZone = eventHook.getValues('TimeZoneNameID')
  const readOnly = props.readonly

  const startTimestamp = Number(eventHook.getValues('StartDate')) * 1000
  const endTimestamp = Number(eventHook.getValues('EndDate')) * 1000

  // Collapsed mode for timed events - single line with clock icon
  // Each element (date, start time, end time) is independently clickable with its own picker
  const tz = timeZone && timeZone.length > 0 ? timeZone : getDefaultTimezone()
  const start = dayjs(startTimestamp).tz(tz)
  const end = dayjs(endTimestamp).tz(tz)
  const sameDay = start.isSame(end, 'day')

  // Determine if should be expanded based on current conditions
  // Multi-day non-all-day events should always be expanded
  const shouldBeExpanded = !sameDay && !isAllDay
  // isExpanded is true if: user manually expanded OR if it's a multi-day non-all-day event
  // isExpanded is false if: all-day is on
  const isExpanded = isAllDay ? false : shouldBeExpanded || expanded

  useLayoutEffect(() => {
    // Keep expanded state in sync for user-triggered expansions
    if (shouldBeExpanded && !expanded) {
      setExpanded(true)
    }
  }, [shouldBeExpanded, expanded])

  const shouldShowEndDate = !isSomeday
  const shouldShowCollapsedTimezone = !isExpanded && !isSomeday && !isAllDay
  const canRenderTimezone =
    !isMobileDevice() &&
    (nonDefaultTimeZone || !readOnly) &&
    !isSomeday &&
    !isAllDay

  const renderPicker = ({
    name,
    pickerType,
    defaultValue,
    showYear = false,
    hideDate = false,
    hideTime = false,
    shouldShowTimeDropdown = false
  }: {
    name: 'StartDate' | 'EndDate'
    pickerType: PickerType
    defaultValue: EventDoc['StartDate'] | EventDoc['EndDate']
    showYear?: boolean
    hideDate?: boolean
    hideTime?: boolean
    shouldShowTimeDropdown?: boolean
  }) => (
    <Controller
      name={name}
      control={eventHook.control}
      defaultValue={defaultValue}
      render={({ field }) => {
        const fieldTimestamp = Number(field.value) * 1000
        const pickerStartTime =
          pickerType === PickerType.START ? fieldTimestamp : startTimestamp
        const pickerEndTime =
          pickerType === PickerType.END ? fieldTimestamp : endTimestamp

        return (
          <EventDatePicker
            shouldShowTimeDropdown={shouldShowTimeDropdown}
            eventHook={eventHook}
            pickerType={pickerType}
            allDay={isAllDay}
            setStartTime={eventHook.setStartTime}
            startTime={pickerStartTime}
            setEndTime={eventHook.setEndTime}
            endTime={pickerEndTime}
            timeZone={timeZone}
            setTimeZone={eventHook.setTimeZone}
            duration={pickerEndTime - pickerStartTime}
            dateFormat={
              hideDate
                ? undefined
                : (ts: number) => formatDate(ts, timeZone, showYear)
            }
            timeFormat={
              hideTime ? undefined : (ts: number) => formatTime(ts, timeZone)
            }
            hideDate={hideDate}
            hideTime={hideTime}
            readOnly={readOnly}
            expanded={isExpanded}
          />
        )
      }}
    />
  )

  const renderTimezone = ({
    open,
    compact
  }: {
    open: boolean
    compact: boolean
  }) => {
    if (!canRenderTimezone) {
      return null
    }

    return (
      <TimezonePopup
        open={open}
        setOpen={setTimeZoneOpen}
        setTimeZone={(tz: string) => {
          eventHook.setTimeZone(tz)
        }}
      >
        <Box
          sx={{
            pointerEvents: readOnly ? 'none' : 'auto',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginLeft:
              compact && i18n.dir() === 'ltr' ? '4px' : compact ? undefined : 0,
            marginRight:
              compact && i18n.dir() !== 'ltr' ? '4px' : compact ? undefined : 0,
            paddingBottom: compact ? '3px' : 0
          }}
          onClick={() => setTimeZoneOpen(true)}
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: nonDefaultTimeZone
                ? theme.palette.text.primary
                : theme.palette.input.placeholder,
              padding: compact ? '3px 4px' : undefined,
              paddingBottom: compact ? undefined : readOnly ? '0px' : '3px',
              '&:hover': {
                backgroundColor: theme.palette.background.paper,
                borderRadius: compact ? '8px' : undefined
              }
            }}
          >
            {nonDefaultTimeZone ? timeZone : t('timeZone')}
          </Typography>
          {nonDefaultTimeZone && !readOnly && (
            <IconButton size="xs" onClick={removeTimeZone}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </TimezonePopup>
    )
  }

  const sectionLabel = (label: string) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 400
      }}
    >
      {label}
    </Typography>
  )

  return (
    <>
      {/* Collapsed view */}
      <Collapse in={!isExpanded} timeout={300}>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          width="100%"
          minHeight="30px"
        >
          {/* Clock icon */}
          <Box
            sx={{
              minWidth: '42px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <ClockIcon
              style={{
                width: 16,
                height: 16,
                color: theme.palette.input.placeholder
              }}
            />
          </Box>

          {/* Date/Time display - each element independently clickable */}
          <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
            {/* Start Date Picker (date only) */}
            {renderPicker({
              name: 'StartDate',
              pickerType: PickerType.START,
              defaultValue: event.StartDate,
              hideTime: true
            })}

            {isAllDay ? null : <Box height={'100%'} width={'0.5rem'} />}
            {/* Start Time Picker (time only) */}
            {renderPicker({
              name: 'StartDate',
              pickerType: PickerType.START,
              defaultValue: event.StartDate,
              hideDate: true,
              shouldShowTimeDropdown: true,
              hideTime: expanded
            })}

            {/* Dash separator for timed events */}
            {shouldShowEndDate && (
              <Typography
                sx={{
                  fontSize: '14px',
                  color: theme.palette.text.primary,
                  mb: 1
                }}
              >
                –
              </Typography>
            )}

            {/* End Time/Date Picker */}
            {!shouldShowEndDate ? null : sameDay || isAllDay ? (
              // Same day: only show end time picker
              renderPicker({
                name: 'EndDate',
                pickerType: PickerType.END,
                defaultValue: event.EndDate,
                hideDate: !isAllDay,
                hideTime: isExpanded,
                shouldShowTimeDropdown: true
              })
            ) : (
              // Different days: show end date and time picker separately
              <>
                {renderPicker({
                  name: 'EndDate',
                  pickerType: PickerType.END,
                  defaultValue: event.EndDate,
                  hideTime: true
                })}
                {renderPicker({
                  name: 'EndDate',
                  pickerType: PickerType.END,
                  defaultValue: event.EndDate,
                  hideDate: true,
                  hideTime: isExpanded,
                  shouldShowTimeDropdown: true
                })}
              </>
            )}
            <Box height={'100%'} width={'0.5rem'} />
            {/* Time zone - show for non all-day events */}
            {shouldShowCollapsedTimezone &&
              renderTimezone({ open: timeZoneOpen, compact: true })}
          </Box>

          {/* Expand button - only show when not expanded */}
          {!isSomeday && !isAllDay && !isExpanded && !readOnly && (
            <MuiIconButton
              size="small"
              onClick={() => setExpanded(true)}
              sx={{
                padding: '4px',
                marginLeft: i18n.dir() === 'ltr' ? '8px' : undefined,
                marginRight: i18n.dir() === 'ltr' ? undefined : '8px',
                '& svg rect': {
                  fill: theme.palette.surface.greyButton as string
                },
                '& svg path': {
                  fill: theme.palette.getContrastText(
                    theme.palette.input.placeholder as string
                  )
                }
              }}
            >
              <MemberDownIcon
                id="expand-time-section"
                style={{
                  width: 24,
                  height: 24
                }}
              />
            </MuiIconButton>
          )}
        </Box>
      </Collapse>

      {/* Expanded view */}
      <Collapse in={isExpanded} timeout={300}>
        <Box>
          {/* Starts row */}
          <PopupSectionRow
            gap="0rem"
            icon={sectionLabel(t('starts'))}
            title={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2}
              >
                {renderPicker({
                  name: 'StartDate',
                  pickerType: PickerType.START,
                  defaultValue: event.StartDate,
                  showYear: true,
                  shouldShowTimeDropdown: true
                })}

                {/* Time zone */}
                {renderTimezone({
                  open: timeZoneOpen && isExpanded,
                  compact: false
                })}
              </Box>
            }
          />

          {/* Ends row */}
          {shouldShowEndDate && (
            <PopupSectionRow
              gap="0rem"
              onlyTitle
              icon={sectionLabel(t('ends'))}
              title={renderPicker({
                name: 'EndDate',
                pickerType: PickerType.END,
                defaultValue: event.EndDate,
                showYear: true,
                shouldShowTimeDropdown: true
              })}
            />
          )}
        </Box>
      </Collapse>
    </>
  )
}
